import {
  AppChannel,
  ChannelOption,
  PaymentInitiation,
  PaymentMethod,
  PaymentModuleConfig,
  PaymentPlan,
  PaymentProvider,
  PaymentProviderId,
  PaymentRequest,
  PaymentReturn,
  PaymentVerification,
  paymentOptionKey,
} from '../types';
import { getAllProviders, getProvider } from '../providers/registry';
import { CHANNEL_OPTIONS } from '../config/defaults';
import { detectChannel, getRouterBasename } from './channel';

/**
 * ─── PaymentService (façade) ─────────────────────────────────────────────────
 *
 * SEUL point d'entrée du reste de l'application vers les paiements.
 * L'app ne connaît jamais FlexPay ni M-Pesa : elle demande au service,
 * qui résout le provider selon (1) le mode mock, (2) le canal d'ouverture
 * (Super App M-Pesa → M-Pesa, sinon → FlexPay), (3) la config admin.
 */
export class PaymentService {
  constructor(private readonly getConfig: () => PaymentModuleConfig) {}

  get channel(): AppChannel {
    return detectChannel();
  }

  /** Le MockProvider doit-il court-circuiter tous les paiements ? (tests E2E). */
  private get mockActive(): boolean {
    const config = this.getConfig();
    return config.mockMode && config.providers[PaymentProviderId.MOCK];
  }

  /**
   * Options de paiement RÉELLEMENT proposables sur le canal courant :
   *   - la méthode est activée en admin (kill-switch de maintenance),
   *   - son provider est activé (ou mode Mock actif),
   *   - le module n'est pas en maintenance globale.
   * Liste vide → l'écran de paiement affiche « momentanément indisponible ».
   */
  getAvailableOptions(): ChannelOption[] {
    const config = this.getConfig();
    if (config.maintenanceMode) return [];
    const mock = this.mockActive;
    return (CHANNEL_OPTIONS[this.channel] ?? []).filter(
      (o) =>
        config.optionEnabled?.[paymentOptionKey(o.method, o.provider)] !== false &&
        (mock || config.providers[o.provider]),
    );
  }

  /**
   * Provider effectif pour une méthode SUR LE CANAL COURANT. Le même moyen
   * (ex: carte bancaire) route vers des providers différents selon le canal.
   * mockMode → MockProvider (tout le flow sans backend).
   */
  resolveProviderFor(method: PaymentMethod): PaymentProvider | null {
    const config = this.getConfig();
    if (this.mockActive) return getProvider(PaymentProviderId.MOCK);

    const option = (CHANNEL_OPTIONS[this.channel] ?? []).find(
      (o) => o.method === method,
    );
    if (!option || !config.providers[option.provider]) return null;
    // Respecte le kill-switch par option (une carte peut être coupée sur un
    // fournisseur sans l'être sur l'autre).
    if (config.optionEnabled?.[paymentOptionKey(option.method, option.provider)] === false) {
      return null;
    }
    return getProvider(option.provider);
  }

  /**
   * Génère une référence de transaction unique côté app.
   * ⚠ FlexPay limite le champ `reference` à 25 caractères (carte doc v1.2 ET
   * mobile money v1.5) → on borne agressivement : `SUB-` + 3 lettres de plan +
   * timestamp base36 + 4 aléatoires, coupé à 25 par sécurité.
   */
  createReference(planId: string): string {
    const plan = planId.replace(/[^a-zA-Z0-9]/g, '').slice(0, 3).toUpperCase();
    const ts = Date.now().toString(36).toUpperCase();
    const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
    return `SUB-${plan}-${ts}${rand}`.slice(0, 25);
  }

  /** URLs internes de retour, transmises au provider (approve/cancel/decline). */
  buildReturnUrls(providerId: PaymentProviderId, reference: string) {
    // Le basename (préfixe canal) est ré-ajouté ici : `window.location.origin`
    // ignore le basename du routeur, donc le callback provider doit pointer
    // vers `/maxit/payment/return` pour revenir DANS le bon canal.
    const base = `${window.location.origin}${getRouterBasename()}/payment/return`;
    const qs = (outcome: string) =>
      `${base}?provider=${providerId}&outcome=${outcome}&reference=${encodeURIComponent(reference)}`;
    return {
      approve: qs('approved'),
      cancel: qs('cancelled'),
      decline: qs('declined'),
    };
  }

  async initiate(args: {
    plan: PaymentPlan;
    method: PaymentMethod;
    user: { id?: string; name: string; email: string };
    reference: string;
    phone?: string;
  }): Promise<{ provider: PaymentProvider; initiation: PaymentInitiation }> {
    const config = this.getConfig();
    if (!config.enabled) {
      throw new Error('Les paiements sont désactivés.');
    }
    if (config.maintenanceMode) {
      throw new Error(
        'Les paiements sont temporairement en maintenance. Réessayez plus tard.',
      );
    }

    const provider = this.resolveProviderFor(args.method);
    if (!provider) {
      throw new Error('Aucun fournisseur de paiement disponible.');
    }

    const request: PaymentRequest = {
      reference: args.reference,
      plan: args.plan,
      method: args.method,
      user: args.user,
      phone: args.phone,
      returnUrls: this.buildReturnUrls(provider.id, args.reference),
    };

    const initiation = await provider.initiate(request);
    return { provider, initiation };
  }

  async verify(
    providerId: PaymentProviderId,
    orderNumber: string,
  ): Promise<PaymentVerification> {
    return getProvider(providerId).verify(orderNumber);
  }

  /** Interprète une URL de retour, quel que soit le provider émetteur. */
  parseReturn(params: URLSearchParams): {
    providerId: PaymentProviderId;
    result: PaymentReturn;
  } | null {
    for (const provider of getAllProviders()) {
      const result = provider.parseReturn(params);
      if (result) return { providerId: provider.id, result };
    }
    return null;
  }
}
